import { GripVertical, Maximize2, Mic, MicOff, MonitorX, Pause, Play, Video, VideoOff, X } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Avatar, VideoView } from '../components/ui';
import { desktop } from '../lib/desktop';
import { createStore, useStore } from '../lib/store';
import type { MeetingSession, MeetingState as SessionState } from '../meeting/MeetingSession';

// Always-on-top floating window (Document Picture-in-Picture, Chrome/Edge 116+): stays above
// every app while you share, can be moved and resized, and shows cameras plus share controls.
// Browsers without it fall back to the classic video Picture-in-Picture of one camera.
// In the desktop app (desktop/) it becomes two native windows, a control bar and a camera
// panel, that float above every app and are left out of what you share.

interface DocumentPip {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
  window: Window | null;
}
declare global {
  interface Window {
    documentPictureInPicture?: DocumentPip;
  }
}

export const floatingStore = createStore<{ open: boolean }>({ open: false });
export const useFloatingOpen = () => useStore(floatingStore, (s) => s.open);

export function documentPipSupported(): boolean {
  return typeof window !== 'undefined' && !!window.documentPictureInPicture;
}

export function floatingSupported(): boolean {
  return !!desktop || documentPipSupported() || (typeof document !== 'undefined' && document.pictureInPictureEnabled);
}

interface Mounted {
  win: Window;
  root: Root;
}

let pip: Mounted | null = null;
let desktopWindows: Mounted[] = [];
/** Set when opening the desktop windows minimized the meeting window (restored on close). */
let minimizedMain = false;
let stopWatchingShare: (() => void) | null = null;

function copyStyles(target: Document): void {
  for (const sheet of [...document.styleSheets]) {
    try {
      const style = target.createElement('style');
      style.textContent = [...sheet.cssRules].map((r) => r.cssText).join('\n');
      target.head.appendChild(style);
    } catch {
      if (sheet.href) {
        const link = target.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        target.head.appendChild(link);
      }
    }
  }
}

/** Renders `content` into another window of this page (same origin, same JS process). */
function mount(win: Window, bodyClass: string, content: ReactNode, onGone: () => void): Mounted {
  copyStyles(win.document);
  win.document.title = 'Nexo Meet';
  win.document.documentElement.dataset.theme = 'dark';
  win.document.body.className = `meeting-theme ${bodyClass}`;
  const container = win.document.createElement('div');
  container.className = 'floating-root';
  win.document.body.appendChild(container);
  const root = createRoot(container);
  root.render(content);
  win.addEventListener('pagehide', () => {
    root.unmount();
    onGone();
  });
  return { win, root };
}

/** Must be called from a click/tap in browsers (they require a user gesture to open it). */
export async function openFloating(session: MeetingSession, opts: { forShare?: boolean } = {}): Promise<boolean> {
  if (desktop) return openDesktopFloating(session, !!opts.forShare);
  if (pip) return true;
  if (!documentPipSupported()) return openVideoPip();
  let win: Window;
  try {
    win = await window.documentPictureInPicture!.requestWindow({ width: 360, height: 420 });
  } catch {
    return false;
  }
  pip = mount(win, 'floating-body', <FloatingPanel session={session} />, () => {
    pip = null;
    floatingStore.set({ open: false });
  });
  floatingStore.set({ open: true });
  return true;
}

export function closeFloating(): void {
  pip?.win.close();
  for (const m of desktopWindows) m.win.close();
  desktopWindows = [];
  stopWatchingShare?.();
  stopWatchingShare = null;
  if (minimizedMain) {
    minimizedMain = false;
    void desktop?.mainWindow('restore');
  }
  if (!pip) floatingStore.set({ open: false });
  if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => undefined);
}

/** Fallback (Safari, Firefox): the browser's own floating player with one remote camera. */
async function openVideoPip(): Promise<boolean> {
  if (!document.pictureInPictureEnabled) return false;
  const videos = [...document.querySelectorAll<HTMLVideoElement>('.tile[data-video="on"] video')];
  const target = videos.find((v) => !v.closest('[aria-label$="(Yo)"]')) ?? videos[0];
  if (!target) return false;
  try {
    await target.requestPictureInPicture();
    return true;
  } catch {
    return false;
  }
}

// ── Desktop app: control bar + camera panel ─────────────────────────────

function openDesktopFloating(session: MeetingSession, forShare: boolean): boolean {
  if (!desktopWindows.length) {
    // The app turns these blank children into always-on-top, frameless, capture-proof windows.
    const bar = window.open('about:blank', 'nexo-bar', 'popup,width=560,height=56');
    const cams = window.open('about:blank', 'nexo-cameras', 'popup,width=248,height=430');
    if (!bar || !cams) {
      bar?.close();
      cams?.close();
      return false;
    }
    const gone = (win: Window) => () => {
      desktopWindows = desktopWindows.filter((m) => m.win !== win);
      if (!desktopWindows.length) closeFloating();
    };
    desktopWindows = [
      mount(bar, 'desktop-floating', <DesktopBar session={session} />, gone(bar)),
      mount(cams, 'desktop-floating', <DesktopCameras session={session} />, gone(cams)),
    ];
    floatingStore.set({ open: true });
  }
  if (forShare && !stopWatchingShare) {
    minimizedMain = true;
    void desktop!.mainWindow('minimize');
    // Like Zoom: when the share ends, the floating windows go away and the meeting comes back.
    stopWatchingShare = session.store.subscribe(() => {
      if (!session.store.get().screenStream) closeFloating();
    });
  }
  return true;
}

/** Brings the meeting window back without stopping the share or closing the floating windows. */
function showMeetingWindow(): void {
  minimizedMain = false;
  void desktop?.mainWindow('restore');
}

// ── Shared pieces ───────────────────────────────────────────────────────

function FloatingTile({ name, id, stream, videoOn, muted, speaking, mirror }: { name: string; id: string; stream: MediaStream | null; videoOn: boolean; muted: boolean; speaking: boolean; mirror: boolean }) {
  return (
    <div className={`fl-tile ${speaking ? 'speaking' : ''}`} data-name={name}>
      {videoOn && stream ? <VideoView stream={stream} mirror={mirror} /> : <Avatar name={name} seed={id} size={44} />}
      <span className="fl-name">
        {muted && <MicOff size={11} />} {name}
      </span>
    </div>
  );
}

function shareLabel(s: SessionState): string {
  const what = s.shareKind === 'image' ? 'una imagen' : 'tu pantalla';
  return s.sharePaused ? 'Pantalla compartida en pausa' : `Compartiendo ${what}`;
}

/** Cameras: the person speaking first, then by arrival; yourself last. */
function FloatingTiles({ s, max }: { s: SessionState; max: number }) {
  const selfStream = useMemo(() => (s.cameraTrack ? new MediaStream([s.cameraTrack]) : null), [s.cameraTrack]);
  const self = s.participants[s.selfId!];
  const others = Object.values(s.participants)
    .filter((p) => p.id !== s.selfId)
    .sort((a, b) => Number(b.id === s.activeSpeakerId) - Number(a.id === s.activeSpeakerId) || a.joinedAt - b.joinedAt);
  const shown = others.slice(0, max);
  return (
    <div className="fl-grid">
      {shown.map((p) => (
        <FloatingTile
          key={p.id}
          id={p.id}
          name={p.name}
          stream={p.cameraStreamId ? (s.remoteStreams[p.cameraStreamId] ?? null) : null}
          videoOn={p.videoOn}
          muted={p.audioMuted}
          speaking={!!s.speaking[p.id] && !p.audioMuted}
          mirror={false}
        />
      ))}
      {self && <FloatingTile id={self.id} name={`${self.name} (Yo)`} stream={selfStream} videoOn={s.videoOn} muted={s.audioMuted} speaking={!!s.speaking[self.id]} mirror />}
      {others.length > shown.length && <div className="fl-more">+{others.length - shown.length}</div>}
    </div>
  );
}

function FloatingChat({ s }: { s: SessionState }) {
  const lastMessage = s.messages.at(-1);
  if (!(s.unread > 0 && lastMessage)) return null;
  return (
    <div className="fl-chat" role="status">
      <strong>{lastMessage.fromName}:</strong> {lastMessage.text.slice(0, 90)}
      <span className="fl-badge">{s.unread}</span>
    </div>
  );
}

function MediaButtons({ session, s }: { session: MeetingSession; s: SessionState }) {
  return (
    <>
      <button type="button" className={`fl-btn ${s.audioMuted ? 'off' : ''}`} onClick={() => void session.toggleAudio()} aria-label={s.audioMuted ? 'Activar audio' : 'Silenciar'}>
        {s.audioMuted ? <MicOff size={18} /> : <Mic size={18} />}
      </button>
      <button type="button" className={`fl-btn ${s.videoOn ? '' : 'off'}`} onClick={() => void session.toggleVideo()} aria-label={s.videoOn ? 'Detener vídeo' : 'Iniciar vídeo'}>
        {s.videoOn ? <Video size={18} /> : <VideoOff size={18} />}
      </button>
    </>
  );
}

function ShareButtons({ session, s }: { session: MeetingSession; s: SessionState }) {
  return (
    <>
      <button type="button" className="fl-btn" onClick={() => session.setSharePaused(!s.sharePaused)} aria-label={s.sharePaused ? 'Reanudar' : 'Pausar'}>
        {s.sharePaused ? <Play size={18} /> : <Pause size={18} />}
        <span>{s.sharePaused ? 'Reanudar' : 'Pausar'}</span>
      </button>
      <button type="button" className="fl-btn stop" onClick={() => session.stopShare()} aria-label="Dejar de compartir">
        <MonitorX size={18} />
        <span>Dejar de compartir</span>
      </button>
    </>
  );
}

const Ended = () => <div className="fl-ended">La reunión ha terminado. Puedes cerrar esta ventana.</div>;

/** Contents of the browser floating window. Rendered in its own React root inside the PiP document. */
function FloatingPanel({ session }: { session: MeetingSession }) {
  const s = useStore(session.store);
  if (!s.selfId || s.phase !== 'in-meeting') return <Ended />;
  const sharing = !!s.screenStream;

  return (
    <div className="fl">
      <header className={`fl-head ${sharing ? (s.sharePaused ? 'paused' : 'live') : ''}`}>
        <span className="fl-dot" />
        <strong>{sharing ? shareLabel(s) : (s.meeting?.title ?? 'Reunión')}</strong>
        <span className="fl-count">{Object.keys(s.participants).length} en la reunión</span>
      </header>
      <FloatingTiles s={s} max={3} />
      <FloatingChat s={s} />
      <footer className="fl-bar">
        <MediaButtons session={session} s={s} />
        {sharing && <ShareButtons session={session} s={s} />}
      </footer>
    </div>
  );
}

/** Desktop control bar: one row that fits its window exactly (the window is sized to it). */
function DesktopBar({ session }: { session: MeetingSession }) {
  const s = useStore(session.store);
  const ref = useRef<HTMLDivElement>(null);
  const sharing = !!s.screenStream;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = Math.ceil(el.offsetWidth);
    const h = Math.ceil(el.offsetHeight);
    if (w && h && (w !== el.ownerDocument.defaultView?.innerWidth || h !== el.ownerDocument.defaultView?.innerHeight)) void desktop?.fitFloating('nexo-bar', w, h);
  });

  if (!s.selfId || s.phase !== 'in-meeting') return <Ended />;
  return (
    <div ref={ref} className="dfl-bar" role="toolbar" aria-label="Controles de la reunión">
      <span className="dfl-grip" aria-hidden title="Arrastra para mover">
        <GripVertical size={16} />
      </span>
      <span className={`dfl-status ${sharing ? (s.sharePaused ? 'paused' : 'live') : ''}`} role="status">
        <span className="fl-dot" />
        {sharing ? shareLabel(s) : (s.meeting?.title ?? 'Reunión')}
      </span>
      <MediaButtons session={session} s={s} />
      {sharing && <ShareButtons session={session} s={s} />}
      <button type="button" className="fl-btn" onClick={() => (sharing ? showMeetingWindow() : closeFloating())} aria-label={sharing ? 'Mostrar la reunión' : 'Cerrar la barra flotante'} title={sharing ? 'Mostrar la ventana de la reunión' : 'Cerrar'}>
        {sharing ? <Maximize2 size={16} /> : <X size={16} />}
      </button>
    </div>
  );
}

/** Desktop camera panel: everyone's video in a column; resizable and movable from its header. */
function DesktopCameras({ session }: { session: MeetingSession }) {
  const s = useStore(session.store);
  if (!s.selfId || s.phase !== 'in-meeting') return <Ended />;
  return (
    <div className="dfl-cams">
      <header className="dfl-cams-head" title="Arrastra para mover">
        <GripVertical size={14} />
        <span>{Object.keys(s.participants).length} en la reunión</span>
      </header>
      <FloatingTiles s={s} max={8} />
      <FloatingChat s={s} />
    </div>
  );
}
